import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { formatCurrency, formatDate, todayET } from '../utils/formatters';
import IconCsvButton from '../components/IconCsvButton';

const PERIODS = [
  { key: 'today',      label: 'Today' },
  { key: 'week',       label: 'This Week' },
  { key: 'month',      label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom',     label: 'Custom' },
  { key: 'lifetime',   label: 'Lifetime' },
];

export default function BreakerCommission() {
  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState(todayET());
  const [customEnd, setCustomEnd] = useState(todayET());
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUserIds, setExpandedUserIds] = useState(() => new Set());

  const toggleExpanded = (userId) => {
    setExpandedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const { data } = await api.get('/api/admin/breaker-commission', { params });
      setRows(data.rows);
      setRange(data.range);
    } catch (err) {
      console.error('Load breaker commission error:', err);
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  const updateConfig = async (userId, patch) => {
    try {
      await api.put(`/api/admin/breaker-commission/${userId}`, patch);
      load();
    } catch (err) {
      console.error('Update config error:', err);
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const exportUrl = (() => {
    const qs = new URLSearchParams({ period });
    if (period === 'custom') { qs.set('start', customStart); qs.set('end', customEnd); }
    return `/api/admin/breaker-commission/export?${qs.toString()}`;
  })();

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Breaker Commission</h2>
        <IconCsvButton label="Export CSV" href={exportUrl} />
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ color: 'var(--text-light)', fontSize: 13 }}>Period:</span>
          {PERIODS.map(p => (
            <button key={p.key}
              className={`btn btn-sm ${period === p.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4 }} />
              <span style={{ color: 'var(--text-light)' }}>→</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4 }} />
            </>
          )}
          {range && (
            <span style={{ marginLeft: 12, color: 'var(--text-light)', fontSize: 12 }}>
              {formatDate(range.start)} → {formatDate(range.end)}
            </span>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-light)' }}>Loading…</div>
        ) : (
          <div className="table-container sticky-col">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Breaker</th>
                  <th style={{ textAlign: 'center' }}>%</th>
                  <th style={{ textAlign: 'center' }}>Basis</th>
                  <th style={{ textAlign: 'right' }}>Net Sales</th>
                  <th style={{ textAlign: 'right' }}>Net Profit</th>
                  <th style={{ textAlign: 'right' }}>Commission</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const isExpanded = expandedUserIds.has(r.user_id);
                  return (
                    <React.Fragment key={r.user_id}>
                      <tr>
                        <td style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none', color: 'var(--text-light)' }}
                          onClick={() => toggleExpanded(r.user_id)}
                          title={isExpanded ? 'Hide breaks' : 'Show breaks'}>
                          <span style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: 12 }}>&#9654;</span>
                        </td>
                        <td style={{ cursor: 'pointer' }} onClick={() => toggleExpanded(r.user_id)}>
                          {r.name}{' '}
                          <span style={{ color: 'var(--text-light)', fontSize: 12 }}>
                            ({r.period_break_count} breaks)
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input type="number" min={0} max={100} step="0.1" value={r.commission_pct}
                            onChange={e => setRows(prev => prev.map(x => x.user_id === r.user_id ? { ...x, commission_pct: e.target.value } : x))}
                            onBlur={e => updateConfig(r.user_id, { commission_pct: Number(e.target.value), basis: r.basis })}
                            style={{ width: 70, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'right' }} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <select value={r.basis}
                            onChange={e => {
                              const newBasis = e.target.value;
                              setRows(prev => prev.map(x => x.user_id === r.user_id ? { ...x, basis: newBasis } : x));
                              updateConfig(r.user_id, { commission_pct: Number(r.commission_pct), basis: newBasis });
                            }}
                            style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4 }}>
                            <option value="net_profit">Net Profit</option>
                            <option value="net_sales">Net Sales</option>
                          </select>
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(r.period_net_sales)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(r.period_net_profit_floored)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.period_commission)}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td></td>
                          <td colSpan={6} style={{ padding: '8px 12px 14px', background: 'var(--card-bg, #fafafa)' }}>
                            {(!r.breaks || r.breaks.length === 0) ? (
                              <div style={{ color: 'var(--text-light)', fontSize: 13, padding: '4px 0' }}>No breaks in this date range.</div>
                            ) : (
                              <>
                                <table style={{ width: '100%', fontSize: 13 }}>
                                  <thead>
                                    <tr style={{ color: 'var(--text-light)' }}>
                                      <th style={{ textAlign: 'left',  padding: '4px 8px' }}>Break #</th>
                                      <th style={{ textAlign: 'left',  padding: '4px 8px' }}>Date</th>
                                      <th style={{ textAlign: 'left',  padding: '4px 8px' }}>Name</th>
                                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Net Sales</th>
                                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Net Profit</th>
                                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Commission</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.breaks.map(br => (
                                      <tr key={br.id}>
                                        <td style={{ padding: '3px 8px' }}>{br.break_number || '--'}</td>
                                        <td style={{ padding: '3px 8px' }}>{formatDate(br.date)}</td>
                                        <td style={{ padding: '3px 8px' }}>{br.name}</td>
                                        <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(br.net_sales)}</td>
                                        <td style={{ padding: '3px 8px', textAlign: 'right', color: br.net_profit < 0 ? 'var(--danger)' : undefined }}>{formatCurrency(br.net_profit)}</td>
                                        <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(br.commission)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div style={{ color: 'var(--text-light)', fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>
                                  Commission floors net profit at $0 per break — loss-making breaks pay $0.
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-light)', padding: 24 }}>
                      No breakers with completed breaks yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
